import { useEffect, useState } from "react";
import { useAdminSettings, useUpdateAdminSettings } from "../../api/admin";
import { notify } from "./notify";
import { Badge, Button, Card, Loading, PageHeader, SelectField } from "./ui";

const formatPrice = (price: number) =>
  `${price.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} $`;

export const AdminSettings = () => {
  const { data, isLoading } = useAdminSettings();
  const update = useUpdateAdminSettings();
  const [model, setModel] = useState("");
  const [effort, setEffort] = useState("");
  const [pdfSize, setPdfSize] = useState("");

  useEffect(() => {
    if (data) {
      setModel(data.claude.model);
      setEffort(data.claude.effort);
      setPdfSize(data.pdf.size);
    }
  }, [data]);

  if (isLoading || !data || !model) return <Loading />;

  const { models } = data.claude;
  const selected = models[model];

  // An effort the newly picked model doesn't support falls back to its default
  const pickModel = (id: string) => {
    setModel(id);
    if (!models[id].efforts.includes(effort)) {
      setEffort(models[id].default_effort);
    }
  };

  const changed = model !== data.claude.model || effort !== data.claude.effort;

  return (
    <>
      <PageHeader
        title="Réglages"
        subtitle="Modèle Claude utilisé pour écrire les livres et machine qui génère les PDF. S'applique aux prochaines générations ; la page d'un livre permet aussi de relancer avec un autre choix."
      />

      <Card title="Modèle">
        <div className="p-5 space-y-3">
          {Object.entries(models).map(([id, config]) => (
            <label
              key={id}
              className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer ${
                id === model
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="model"
                checked={id === model}
                onChange={() => pickModel(id)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">
                    {config.label}
                  </span>
                  <span className="text-xs text-gray-400 font-mono">{id}</span>
                  {id === data.claude.model && (
                    <Badge color="green">Actuel</Badge>
                  )}
                  {id === data.claude.default_model && (
                    <Badge color="gray">Par défaut</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {formatPrice(config.input_price)} /{" "}
                  {formatPrice(config.output_price)} par million de tokens
                  (entrée / sortie) · en batch :{" "}
                  {formatPrice(config.input_price / 2)} /{" "}
                  {formatPrice(config.output_price / 2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">{config.note}</p>
              </div>
            </label>
          ))}

          <div className="flex items-end gap-3 pt-2">
            <div className="w-48">
              <SelectField
                label="Effort"
                value={effort}
                onChange={setEffort}
                options={selected.efforts.map((e) => [
                  e,
                  e === selected.default_effort ? `${e} (défaut)` : e,
                ])}
              />
            </div>
            <Button
              variant="primary"
              onClick={() =>
                update.mutate(
                  { claude_model: model, claude_effort: effort },
                  notify(`Livres écrits avec ${selected.label} (${effort})`),
                )
              }
              disabled={update.isLoading || !changed}
              confirm={`Écrire les prochains livres avec ${selected.label}, effort ${effort} ?`}
            >
              Enregistrer
            </Button>
          </div>
          <p className="text-xs text-gray-400">
            L'effort règle la réflexion du modèle : plus il est haut, meilleur
            et plus cher est le résultat.
          </p>
        </div>
      </Card>

      <div className="mt-6">
        <Card title="PDF">
          <div className="p-5 space-y-3">
            <div className="flex items-end gap-3">
              <div className="w-48">
                <SelectField
                  label="Taille du dyno"
                  value={pdfSize}
                  onChange={setPdfSize}
                  options={data.pdf.sizes.map((s) => [
                    s,
                    s === data.pdf.default_size ? `${s} (défaut)` : s,
                  ])}
                />
              </div>
              <Button
                variant="primary"
                onClick={() =>
                  update.mutate(
                    { pdf_size: pdfSize },
                    notify(`PDF générés sur ${pdfSize}`),
                  )
                }
                disabled={update.isLoading || pdfSize === data.pdf.size}
                confirm={`Générer les prochains PDF sur un dyno ${pdfSize} ?`}
              >
                Enregistrer
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              Chaque PDF tourne sur un dyno Heroku dédié, facturé à la seconde.
              Une taille au-dessus ne sert que si la génération manque de
              mémoire.
            </p>
          </div>
        </Card>
      </div>
    </>
  );
};
